import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from '@mui/icons-material';

import Navigation from '@/components/Navigation/Navigation';
import ParentAppChrome from '@/components/Parent/ParentAppChrome';
import UserHeader from '@/components/User/UserHeader';
import authSSO from '@/api/authSSO';
import { nfRp } from '@/constants/nf';
import { route } from '@/constants/routeName';
import clearSession from '@/helper/clearSession';
import { schoolDisplayName } from '@/helper/schoolLabel';

const AccContent = ({ label, value }) => {
	return (
		<div className='flex justify-between gap-2 text-sm'>
			<span className='text-[var(--weak)]'>{label}</span>
			<span className='font-semibold text-black'>{value}</span>
		</div>
	);
};

const { signout } = authSSO();

export default function Account() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const innerHeight = useSelector((state) => state.content.innerHeight);
	const c = useSelector((state) => state.auth.selectedCustomer);
	const user = useSelector((state) => state.auth.user);
	const children = useSelector((state) => state.auth.children) || [];
	const accountType = useSelector((state) => state.auth.accountType);
	const selectedOutlet = useSelector((state) => state.auth.selectedOutlet);
	const parentOutlet = useSelector((state) => state.auth.parentOutlet);
	const key = useSelector((state) => state.auth.key);
	const [loading, setLoading] = useState(false);

	const school = schoolDisplayName({ selectedOutlet, parentOutlet });
	const isParent = accountType === 'parent';

	const onSignOut = () => {
		setLoading(true);
		signout(key)
			.then(() => {
				clearSession(dispatch, queryClient);
				navigate('/login');
			})
			.catch((err) => {
				toast.error(err.response?.data?.detail || 'Error');
				setLoading(false);
			});
	};

	if (isParent) {
		return (
			<div style={{ minHeight: innerHeight }} className='parent-ui'>
				<ParentAppChrome />
				<main className='parent-main'>
					<h1>Family account</h1>
					<p className='muted'>
						{user?.name || 'Parent'}
						{school ? ` · ${school}` : ''}
					</p>

					{children.length === 0 && (
						<p className='muted' style={{ marginTop: 20 }}>
							No children linked to this account yet.
						</p>
					)}

					{children.map((child) => {
						const name = child.member?.name || `Child #${child.id}`;
						const klass = child.member?.on_list_attributes?.class;
						return (
							<section key={child.id} className='accountrow'>
								<div className='row'>
									<div>
										<h2>{name}</h2>
										<p className='muted'>
											{klass || '—'} · Available {nfRp(+child.balance || 0)}
										</p>
									</div>
									<Link
										to={`/${route.accountTopUp}`}
										className='topup-btn'
										onClick={() =>
											dispatch({
												type: 'auth/setSelectedCustomerId',
												payload: child.id,
											})
										}
									>
										Top up
									</Link>
								</div>
							</section>
						);
					})}

					<section className='account-settings' aria-label='Account settings'>
						<h3>Account settings</h3>
						<div className='accountrow'>
							<Link to={`/${route.accountPassword}`}>Update password</Link>
						</div>
						<div className='accountrow'>
							<Link to={`/${route.accountTopUps}`}>My top-up history</Link>
						</div>
					</section>

					<button type='button' className='main account-signout' onClick={onSignOut}>
						Sign out
						{loading && (
							<CircularProgress className='ml-2' size={18} sx={{ color: 'var(--white)' }} />
						)}
					</button>
				</main>
				<Navigation />
			</div>
		);
	}

	return (
		<div style={{ minHeight: innerHeight }} className='app-container app-page'>
			<div className='flex flex-col w-full h-full gap-6 p-4 pb-24'>
				<UserHeader hideChildSelector />
				{!c && (
					<p className='text-sm text-[var(--weak)]'>
						Select a student to view their account details.
					</p>
				)}
				{c && (
					<div className='flex flex-col gap-4'>
						{c.member?.name && (
							<p className='text-sm text-[var(--weak)]' data-testid='account-student-name'>
								Student profile for{' '}
								<span className='font-bold text-black'>{c.member.name}</span>
							</p>
						)}
						<AccContent label='Student ID' value={c.member?.on_list_attributes?.student_id} />
						<AccContent label='Class' value={c.member?.on_list_attributes?.class} />
						<div className='flex justify-between'>
							<span>Account password</span>
							<Link
								to={`/${route.accountPassword}`}
								className='flex items-center justify-center'
								aria-label='Update account password'
							>
								<span className='mt-1'>* * * *</span>
								<ChevronRight />
							</Link>
						</div>
						<AccContent
							label='Dietary preferences'
							value={c.member?.on_list_attributes?.dietary_preferences?.join(', ')}
						/>
						<div>
							<AccContent label='Contact' value={c.member?.on_list_attributes?.contact?.name} />
							<AccContent label='' value={c.member?.on_list_attributes?.contact?.phone} />
						</div>
					</div>
				)}
				<Link to={`/${route.accountTopUps}`} className='btn-actions'>
					My Top-Up History
				</Link>
				<button type='button' className='btn-actions' onClick={onSignOut}>
					Sign out{' '}
					{loading && <CircularProgress className='ml-2' size={18} sx={{ color: 'var(--white)' }} />}
				</button>
			</div>
			<Navigation />
		</div>
	);
}
